import { createPersonSchema, updatePersonSchema } from './person.js';
/**
 * Bulk candidate import (issue #40).
 *
 * Parsing and validation live here, not in the API or the form, so the preview a user sees
 * and the decision the server makes come from the SAME code. A client-side preview that
 * disagreed with the server would be worse than no preview.
 *
 * The whole file is validated before anything is written. A partial import that
 * half-succeeds leaves HR reconciling two lists by hand, which is worse than a rejection.
 */
export const CSV_COLUMNS = ['first_name', 'last_name', 'email', 'phone', 'job_profile', 'tags'];
/** RFC 4180-ish: quoted fields, doubled quotes inside them, CRLF or LF. */
export function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    const endField = () => {
        row.push(field);
        field = '';
    };
    const endRow = () => {
        endField();
        rows.push(row);
        row = [];
    };
    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (quoted) {
            if (char === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i += 1;
                }
                else
                    quoted = false;
            }
            else
                field += char;
            continue;
        }
        if (char === '"')
            quoted = true;
        else if (char === ',')
            endField();
        else if (char === '\n')
            endRow();
        else if (char === '\r')
            continue;
        else
            field += char;
    }
    // A trailing newline must not produce a phantom final row.
    if (field !== '' || row.length > 0)
        endRow();
    return rows.filter((r) => r.some((cell) => cell.trim() !== ''));
}
const splitList = (value) => value
    .split(/[;|]/)
    .map((v) => v.trim())
    .filter((v) => v !== '');
export function planCsvImport(text, context) {
    const parsed = parseCsv(text);
    const errors = [];
    if (parsed.length === 0) {
        return { rows: [], errors: [{ line: 1, message: 'The file is empty.' }], duplicateEmails: [] };
    }
    const header = (parsed[0] ?? []).map((h) => h.trim().toLowerCase());
    const missing = CSV_COLUMNS.filter((c) => c !== 'phone' && c !== 'tags').filter((c) => !header.includes(c));
    if (missing.length > 0) {
        return {
            rows: [],
            errors: [{ line: 1, message: `Missing column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}.` }],
            duplicateEmails: [],
        };
    }
    const at = (row, column) => {
        const index = header.indexOf(column);
        return index === -1 ? '' : (row[index] ?? '').trim();
    };
    /**
     * Did this row SAY anything about this column?
     *
     * A column the file omits and a cell the file leaves blank both mean "I am not telling
     * you about this field", never "clear it". The legacy roster HR is migrating from has
     * no attributes column at all and half-filled role and phone columns; under any other
     * reading, one re-import erases the work of a month of data entry (issue #100).
     *
     * Clearing a value is therefore a UI action, not a CSV one.
     */
    const said = (row, column) => at(row, column) !== '';
    const rows = [];
    const seenEmails = new Set();
    const duplicateEmails = [];
    const existing = new Set((context.existingEmails ?? []).map((e) => e.toLowerCase()));
    for (let i = 1; i < parsed.length; i += 1) {
        const raw = parsed[i];
        if (raw === undefined)
            continue;
        const line = i + 1;
        const email = at(raw, 'email').toLowerCase();
        const profile = at(raw, 'job_profile');
        const tags = splitList(at(raw, 'tags'));
        // Unknown configuration values are named, never silently dropped — a typo'd role
        // would otherwise produce a person with an empty checklist and no explanation.
        if (profile !== '' && !context.knownProfiles.includes(profile)) {
            errors.push({ line, message: `Unknown role "${profile}".` });
        }
        for (const tag of tags) {
            if (!context.knownTags.includes(tag))
                errors.push({ line, message: `Unknown tag "${tag}".` });
        }
        if (email !== '' && seenEmails.has(email)) {
            errors.push({ line, message: `"${email}" appears more than once in this file.` });
        }
        seenEmails.add(email);
        if (existing.has(email))
            duplicateEmails.push(email);
        // Built key by key rather than as an object literal, so a column the file did not
        // fill in is ABSENT from the patch rather than present and empty. `first_name`,
        // `last_name` and `email` are the required columns and are always spoken to; a blank
        // one is a row error, exactly as before.
        const draft = {
            name: { givenName: at(raw, 'first_name'), familyName: at(raw, 'last_name') },
            contact: { email: at(raw, 'email') },
        };
        if (said(raw, 'phone')) {
            Object.assign(draft['contact'], { phone: at(raw, 'phone') });
        }
        if (said(raw, 'job_profile'))
            draft['jobProfileKeys'] = [profile];
        if (said(raw, 'tags'))
            draft['tags'] = tags;
        const patch = updatePersonSchema.safeParse(draft);
        if (!patch.success) {
            for (const issue of patch.error.issues) {
                errors.push({ line, message: `${issue.path.join('.') || 'row'}: ${issue.message}` });
            }
            continue;
        }
        // The SAME row with the create schema's defaults applied — derived from the patch, so
        // the row is described in exactly one place and the two shapes can never disagree.
        const candidate = createPersonSchema.safeParse(patch.data);
        if (!candidate.success) {
            for (const issue of candidate.error.issues) {
                errors.push({ line, message: `${issue.path.join('.') || 'row'}: ${issue.message}` });
            }
            continue;
        }
        rows.push({ line, person: candidate.data, patch: patch.data });
    }
    return { rows, errors, duplicateEmails };
}
//# sourceMappingURL=csv.js.map